import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { links } from "@sablier/v2-constants";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { PERMISSION_ID } from "~/client/constants";
import { useModalPermissions, usePermissions } from "~/client/hooks";
import type { IPermissionInfo } from "~/client/types";
import Footer from "./Footer";
import Item from "./Item";

const WrapperPartial = styled.div`
  flex: 1;
  flex-shrink: 0;
  width: 100%;
`;

const Content = styled.div`
  width: 100%;
  padding: ${(props) => props.theme.sizes.edge};
  padding-inline: ${(props) => props.theme.sizes.modalEdge};
`;

const List = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    width: 100%;
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    ${Content} {
      padding: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    }
  }
  ${(props) => props.theme.medias.maxSM} {
    ${Content} {
      padding: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
      ${List} {
        row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
      }
    }
  }
`;

function Body() {
  const { accessor, all, set } = usePermissions();
  const { doClose } = useModalPermissions();
  const { t } = useT();

  const items: Array<IPermissionInfo> = [
    {
      id: PERMISSION_ID.sentry,
      name: t("modalCards.permissions.sentry.title"),
      description: t("modalCards.permissions.sentry.description"),
      link: links.vendor.sentry,
    },
    {
      id: PERMISSION_ID.crisp,
      name: t("modalCards.permissions.crisp.title"),
      description: t("modalCards.permissions.crisp.description"),
      link: links.vendor.crisp,
    },
    {
      id: PERMISSION_ID.mixpanel,
      name: t("modalCards.permissions.mixpanel.title"),
      description: t("modalCards.permissions.mixpanel.description"),
      link: links.vendor.mixpanel,
    },
    {
      id: PERMISSION_ID.vercel,
      name: t("modalCards.permissions.vercel.title"),
      description: t("modalCards.permissions.vercel.description"),
      link: links.vendor.vercel,
    },
  ];

  const initial = useMemo(() => {
    const { permissions } = accessor();
    if (_.has(permissions, "seen") && !permissions.seen) {
      return Object.keys(permissions).reduce(
        (p, c) => ({ ...p, [c]: true }),
        {},
      );
    }
    return permissions;
  }, [accessor]);

  const [session, setSession] = useState<Record<string, boolean>>(initial);

  const toggle = useCallback((id: string) => {
    setSession((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const onConfirm = useCallback(() => {
    set(session);
    doClose();
  }, [doClose, session, set]);

  const onAll = useCallback(() => {
    all(true);
    doClose();
  }, [all, doClose]);

  return (
    <Wrapper>
      <Content>
        <List>
          {items.map((item) => {
            return (
              <Item
                key={item.id}
                info={item}
                value={!!session[item.id]}
                update={toggle}
              />
            );
          })}
        </List>
      </Content>
      <Footer onAll={onAll} onConfirm={onConfirm} />
    </Wrapper>
  );
}

export default Body;
